import React, { ReactNode, useState, useEffect } from "react";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import HelpIcon from "@mui/icons-material/Help";
import DisabledByDefaultIcon from "@mui/icons-material/DisabledByDefault";
import FaceRetouchingNaturalIcon from "@mui/icons-material/FaceRetouchingNatural";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import PolicyIcon from "@mui/icons-material/Policy";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import { useNavigate } from "react-router-dom";
import ApiCommonContent from "../../services/GenericContent";
import ContentTypes from "../../services/Content";
import "./styles.scss";
import { CommonContent, Content, ContentKey } from "../../types/Types";
import UserSession from "../../services/UserSession";
import { GAME_INSTRUCTIVE_PDF } from "../../config/Constants";
import PrivacyPolicyDialog, {
  PrivacyPolicyContent,
} from "../Dialogs/PrivacyPolicy";
import ContentUtil from "./../../util/Content";
import GameHelper from "../../services/GameHelper";

export type NavRoute = {
  label: string;
  icon: ReactNode;
  onClick?: () => void;
  route?: string;
  url?: string;
};

const logOut = async () => {
  return UserSession.endSession();
};

const openPdf = async () => {
  window.open(GAME_INSTRUCTIVE_PDF, "_blank");
};

const optionsGroup3: Array<NavRoute> = [
  { label: "Cerrar sesión", icon: <DisabledByDefaultIcon />, route: "/" },
];

const MainMenu = () => {
  const [commonContent, setCommonContent] = React.useState<
    CommonContent[] | null
  >(null);
  const [contentType, setContentType] = React.useState<Content[] | null>(null);

  const [privacyPolicy, setPrivacyPolicy] =
    React.useState<PrivacyPolicyContent>();
  const [gameInstructions, setGameInstructions] =
    React.useState<PrivacyPolicyContent>();
  const [showPrivacyPolicy, setShowPrivacyPolicy] = React.useState(false);
  const [showGameInstructions, setShowGameInstructions] = React.useState(false);

  useEffect(() => {
    if (!commonContent) {
      ApiCommonContent.getContent().then((commonContent) => {
        ContentTypes.getContent().then((content) => {
          setPrivacyPolicy(
            ContentUtil.toPrivacyPolicyContent(content, commonContent || [])
          );

          setGameInstructions(
            ContentUtil.toGameInstructionsPolicyContent(content, commonContent || [])
          );
          setContentType(content);
        });
        setCommonContent(commonContent);
      });
    }
  });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const toggleDrawer = () => {
    setIsDrawerOpen((prev) => !prev);
  };

  const navigate = useNavigate();

  const optionsGroup1: Array<NavRoute> = [
    {
      label: ApiCommonContent.getKeyValue(
        commonContent || [],
        ContentKey.MY_PROFILE
      ),
      icon: <FaceRetouchingNaturalIcon />,
      route: "/myprofile",
    },
  ];

  const optionsGroup2: Array<NavRoute> = [
    {
      label: ApiCommonContent.getKeyValue(
        commonContent || [],
        ContentKey.HOW_TO_PLAY
      ),
      route: "/howtoplay",
      icon: <HelpIcon />,
    },
    {
      label: ApiCommonContent.getKeyValue(
        commonContent || [],
        ContentKey.GAME_INSTRUCTIONS
      ),
      icon: <MenuBookIcon />,
      onClick: () => {
        setShowGameInstructions(true);
      },
    },
    {
      label: ApiCommonContent.getKeyValue(
        commonContent || [],
        ContentKey.PREFERENCES
      ),
      icon: <SettingsIcon />,
      route: "/settings",
    },
    {
      label: ApiCommonContent.getKeyValue(
        commonContent || [],
        ContentKey.GLOBAL_RANKING
      ),
      route: "/ranking",
      icon: <EmojiEventsIcon />,
    },
    {
      label: ApiCommonContent.getKeyValue(
        commonContent || [],
        ContentKey.PRIVACY_POLICY
      ),
      onClick: () => {
        setShowPrivacyPolicy(true);
      },
      icon: <PolicyIcon />,
    },
  ];

  const optionsGroup3: Array<NavRoute> = [
    {
      label: ApiCommonContent.getKeyValue(
        commonContent || [],
        ContentKey.LOG_OUT
      ),
      icon: <DisabledByDefaultIcon />,
      onClick: async () => {
        logOut();
        GameHelper.removeCurrentGame();
      },
      route: "/",
    },
  ];

  return (
    <Box>
      <IconButton onClick={toggleDrawer} edge="start">
        <MenuIcon className="gameHeaderIconDrawer" />
      </IconButton>
      <Drawer anchor="left" open={isDrawerOpen} onClose={toggleDrawer}>
        <div className="drawerContainer">
          <div className="drawerContainerHeader">
            <h2>
              {ApiCommonContent.getKeyValue(
                commonContent || [],
                ContentKey.MENU
              )}
            </h2>
            <IconButton onClick={toggleDrawer}>
              <CloseIcon />
            </IconButton>
          </div>
          <Divider />
          <List>
            {optionsGroup1.map((option) => (
              <ListItem disablePadding key={option.label}>
                <ListItemButton
                  onClick={() => {
                    navigate(option.route || option.url || "");
                  }}
                >
                  <ListItemIcon className="drawerIcon">
                    {option.icon}
                  </ListItemIcon>
                  <ListItemText primary={option.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <Divider />
          <List>
            {optionsGroup2.map((option) => (
              <ListItem disablePadding key={option.label}>
                <ListItemButton
                  onClick={() => {
                    if (option.onClick) {
                      option.onClick();
                    }
                    if (option.url || option.route) {
                      navigate(option.route || option.url || "");
                    }
                  }}
                >
                  <ListItemIcon className="drawerIcon">
                    {option.icon}
                  </ListItemIcon>
                  <ListItemText primary={option.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <Divider />
          <List>
            {optionsGroup3.map((option) => (
              <ListItem disablePadding key={option.label}>
                <ListItemButton
                  onClick={async () => {
                    if (option.onClick) {
                      await option.onClick();
                    }

                    navigate(option.route || option.url || "");
                  }}
                >
                  <ListItemIcon className="drawerIcon">
                    {option.icon}
                  </ListItemIcon>
                  <ListItemText primary={option.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </div>
      </Drawer>
      {privacyPolicy && (
        <PrivacyPolicyDialog
          hideFooter={true}
          data={privacyPolicy}
          onClose={() => {
            setShowPrivacyPolicy(false);
          }}
          show={showPrivacyPolicy}
          onContinue={() => {
            setShowPrivacyPolicy(false);
          }}
        />
      )}

      {gameInstructions && (
        <PrivacyPolicyDialog
          hideFooter={true}
          data={gameInstructions}
          onClose={() => {
            setShowGameInstructions(false);
          }}
          show={showGameInstructions}
          onContinue={() => {
            setShowGameInstructions(false);
          }}
        />
      )}
    </Box>
  );
};

export default MainMenu;
